import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { CustomRequestConfig } from '@mathflat/shared/@modules/auth/axiosJwtSetup'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { isToday } from 'date-fns'
import { isArray } from 'lodash-es'
import { isObjectLike } from 'lodash-es'

import { LOGIN_API_URLS } from '@/constant'
import { CommonError } from '@/dto/common/CommonError'
import ServerMaintenanceService from '@/page/etc/ServerMaintenance/_service/ServerMaintenance.service'
import globalLoaderService from '@/service/globalLoader.service'
import { localStorageService } from '@/service/storage.service'
import WebviewService from '@/service/webview.service'
import type { ResErr } from '@/types/common/api'
import { emptyStringToNull } from '@/utils/common'
import { envUtils } from '@/utils/envUtils'
import { reporter } from '@/utils/modules/errorReporting/reporter'
import { toastError } from '@/utils/toast'
import { apiTrack, errorTrack } from '@/utils/trackings/apiTracking'

import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from '../jwtInterceptors'
import { createCaptchaGate, isCaptchaError } from './captchaGate'
import { readErrorBody } from './readErrorBody'
import { serializeParams } from './serializeParams'

const BASE_URL = (() => {
  if (envUtils.isStaging) {
    return `https://api-staging.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isLive) {
    return `https://api.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://api-dev.${domainProvider.TARGET_DOMAIN}`
})()

/**
 * Teacher 앱 전용 meta 옵션을 추가한 CustomRequestConfig
 */
export interface TeacherCustomRequestConfig extends CustomRequestConfig {
  meta?: {
    useAbortController: boolean
    customUniqueKey?: string
  }
  /** mp_event_name 으로 쏘는 API 이벤트에 request/response 외의 프로퍼티를 더 싣는다. 서버로는 가지 않는다 */
  mpEventProps?: Record<string, unknown>
}

export type CustomRequestConfigMeta = {
  useAbortController: boolean
  customUniqueKey?: string
}

const preventDuplicateRequest = (
  (
    request: Record<
      string,
      { abortController: AbortController; prevConfigData: TeacherCustomRequestConfig }
    >,
  ) =>
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (!(config as TeacherCustomRequestConfig).meta?.useAbortController) return config

    const method = config.method?.toUpperCase() ?? 'GET'
    const url = config.url?.split('?')[0] ?? ''

    const requestKey = (config as TeacherCustomRequestConfig).meta?.customUniqueKey || method + url
    const currentRequest = request[requestKey]

    if (currentRequest) {
      currentRequest.abortController.abort()
    }

    request[requestKey] = {
      abortController: new AbortController(),
      prevConfigData: config as TeacherCustomRequestConfig,
    }

    return {
      ...config,
      signal: request[requestKey].abortController.signal,
    }
  }
)({})

const maxios: AxiosInstance & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any, R = AxiosResponse<T>>(url: string, config?: TeacherCustomRequestConfig): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: TeacherCustomRequestConfig,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: TeacherCustomRequestConfig,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: TeacherCustomRequestConfig,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any, R = T, _D = any>(url: string, config?: TeacherCustomRequestConfig): Promise<R>
} = axios.create({
  baseURL: BASE_URL,
  headers: {
    'content-Type': 'application/json',
  },

  paramsSerializer: serializeParams,
})

export const getXPlatform = () => {
  if (WebviewService.isAndroid) return 'TEACHER_ANDROID'
  if (WebviewService.isIpad) return 'TEACHER_IOS'
  return 'TEACHER_WEB'
}

maxios.defaults.headers.common['x-platform'] = getXPlatform()
maxios.defaults.headers.common['x-freewheelin-host'] = envUtils.isSchool
  ? 'schoolflat.com'
  : domainProvider.TARGET_DOMAIN

maxios.interceptors.request.use(
  wrapRequestInterceptorWithJwt((config) => {
    //오늘 처음 쓰는 경우 - localstorage에 기록하고 ping 보내기 (login api, whalespace는 제외)
    if (
      !Object.values(LOGIN_API_URLS).includes(config.url || '') &&
      !config.url?.includes('/password') &&
      !/\/academies\/D\d+\/subscription-offer/.test(config.url || '')
    ) {
      const todayFirstUseHistory = localStorageService.getItem('TODAY_FIRST_USE_HISTORY')
      if (!todayFirstUseHistory || !isToday(new Date(todayFirstUseHistory))) {
        localStorageService.setItem('TODAY_FIRST_USE_HISTORY')(new Date().getTime())
      }
    }

    if (config.data instanceof FormData) {
      return preventDuplicateRequest(config)
    }

    // data에서 emptyString("")은 null로 보냄
    if (!isArray(config.data) && isObjectLike(config.data)) {
      config.data = emptyStringToNull(config.data)
    }

    return preventDuplicateRequest(config)
  }),
)

const CAPTCHA_LIMITED_MESSAGE = '이용이 잠시 제한되었어요. 잠시 후 다시 시도해 주세요.'

// 캡차 모달은 modal.service → @/constant 배럴을 거쳐 다시 maxios 에 닿는다. 정적으로 import 하면 순환에
// 간선을 더하고 모달 UI 를 maxios 와 함께 싣게 되므로, 캡차가 필요할 때만 불러온다
const captchaGate = createCaptchaGate({
  // 청크 로드 실패에 lazyWithRecovery 를 쓰지 않는다. 그 복구는 끝내 새로고침이라 만들던 학습지·교재를 잃는다
  requestPass: async () => {
    globalLoaderService.setHiddenForModal(true)
    try {
      const { openCaptchaModal } = await import('@/contentComponents/modal/CaptchaModal')
      return await openCaptchaModal()
    } catch (error) {
      reporter.capture(error, { mechanism: 'interceptor' })
      return Promise.reject(error)
    } finally {
      globalLoaderService.setHiddenForModal(false)
    }
  },
  notifyLimited: () => toastError(CAPTCHA_LIMITED_MESSAGE),
})

type CaptchaRetryConfig = InternalAxiosRequestConfig & { captchaRetried?: boolean }

maxios.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data.error) {
      window.alert(response.data.error)
      return Promise.reject(response.data.error)
    }
    apiTrack(response)

    return response
  },
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<ResErr>

      errorTrack(axiosError)

      // 던지는 객체를 그대로 파이프라인에 넘긴다. 취소를 포함해 버릴지는 파이프라인만 정한다(D-11).
      if (axiosError.code === 'ERR_CANCELED') {
        reporter.capture(axiosError, { mechanism: 'interceptor' })
        return Promise.reject(axiosError)
      }

      if (axiosError.response) {
        const { config, status } = axiosError.response
        const { message, code, data } = await readErrorBody(axiosError.response.data)

        const err = new CommonError(message ?? '', { type: 'API', status, code, data, config })

        if (status >= 501) {
          await ServerMaintenanceService.checkServerMaintenance()
        }

        reporter.capture(err, { mechanism: 'interceptor' })

        if (isCaptchaError(err)) {
          const captchaConfig = config as CaptchaRetryConfig
          return captchaGate.handle(err, {
            retried: !!captchaConfig.captchaRetried,
            retry: () => maxios({ ...captchaConfig, captchaRetried: true } as CaptchaRetryConfig),
          })
        }

        return Promise.reject<CommonError>(err)
      } else {
        // 응답 없는 실패는 메시지에 URL 이 없어 config 가 유일한 단서다. 없으면 모든 route 의 타임아웃이 한 이슈로 뭉친다.
        const err = new CommonError(axiosError.message, {
          type: 'network',
          code: axiosError.code,
          config: axiosError.config,
        })
        reporter.capture(err, { mechanism: 'interceptor' })
        return Promise.reject<CommonError>(err)
      }
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return maxios(originalRequest)
    },
  ),
)

export const getResource = async (url: string) => {
  const response = await axios.get(url)
  if (response.status !== 200) throw new Error(response.statusText)
  return response.data
}

export default maxios
